// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { ConfigCrsFetchNon200Response, CrsFetchTimeout, ResolverAppErrors, buildNumberRegex, getBuildNumberAsync, getCacheKeyFromConfigRef, getConfigRefFromKey } from "../utilities";
import { createError } from "@msnews/web-worker-shared-utils";
import { isEmpty } from "lodash-es";
/**
 * Config service class
 * This interacts with CRS and gets resolved and merged config
 */
export class ConfigService {
    /**
     * Create config service based on ResolverEnvironment
     * @param {ResolverEnvironment} resolverEnvironment
     */
    constructor(resolverEnvironment) {
        this.resolverEnvironment = resolverEnvironment;
        /**
         * Dictionary to maintain all resolved config for internal state management
         * @type {Map<string, ExperienceConfig>}
         */
        this.configMap = new Map();
        /**
         * True if config service is enabled else false.
         */
        this.enableConfigService = true;
        /**
         * Dictionary to maintain all promise objects which are not resolved/rejected. Once promise object is resolved/rejected,
         * it gets deleted from this dictionary and put in the configMap.
         * @type {Map<string, Promise<ExperienceConfig>>}
         */
        this.configPromiseMap = new Map();
        /**
         * If config service returns specific error code, then try fallback resolver.
         */
        this._shouldTryFallbackResolver = false;
        /**
         * If CRS fetch had network error
         */
        this._hadNetworkError = false;
        /**
         * Helper to avoid get cache key for the given configRef and the current resolver environment
         * @param configRef The config reference for which to get the cache key
         * @returns {string} The cache key that uniquely represents the given config reference object.
         */
        this.getCacheKeyFromConfigRef = (configRef) => getCacheKeyFromConfigRef(configRef, this.resolverEnvironment.appType);
        if (!resolverEnvironment
            || !resolverEnvironment.configServiceParameters
            || !resolverEnvironment.appType
            || !resolverEnvironment.configServiceParameters.configServiceBaseUrl
            || !resolverEnvironment.targetScope) {
            this.enableConfigService = false;
            throw new Error("ResolverEnvironment or configServiceParameters or targetScope does not exist.");
        }
    }
    /**
     * If CRS fetch had network error
     */
    get hadNetworkError() {
        return this._hadNetworkError;
    }
    /**
     * If config service returns specific error code, then try fallback resolver.
     */
    get shouldTryFallbackResolver() {
        return this._shouldTryFallbackResolver;
    }
    /**
     * Returns true if config service is enabled
     */
    get configServiceEnabled() {
        return this.enableConfigService;
    }
    /**
     * Returns config service query params
     */
    get configServiceQueryParams() {
        return this.configServiceParams;
    }
    /**
     * Initialize config service
     */
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            this.shouldUseConfigService();
            yield this.setConfigServiceParams();
        });
    }
    /**
     * Set query param from CRS request configurations
     */
    setConfigServiceParams() {
        return __awaiter(this, void 0, void 0, function* () {
            const { appType, configServiceParameters, targetScope } = this.resolverEnvironment;
            const { maxDepth } = configServiceParameters;
            let { buildVersion } = configServiceParameters;
            // Send PCS apptype to CRS so we can build CRS Jarvis dashboards per apptype.
            let configServiceParams = `apptype=${appType}`;
            // Use regex to text whether "spalink" QSP is an actual build number (with the format YYYYMMDD.<ver>) or an alias (for local scenario only).
            // If it is an alias, convert that to a corresponding build number (using the /v endpoint), because CRS can't handle aliases.
            if (__WEBPACK_LOCAL_GITCONFIGS__ && !buildNumberRegex.test(buildVersion)) {
                buildVersion = yield getBuildNumberAsync(buildVersion, appType);
            }
            // Add client build version
            configServiceParams += `&v=${buildVersion}`;
            // Query string parameter from target scope.
            configServiceParams += "&targetScope=" + JSON.stringify(targetScope);
            // Add maxDepth if exist. Mostly for dev scenario
            if (maxDepth) {
                configServiceParams += `&maxDepth=${maxDepth}`;
            }
            // If url has dev only query string params, inform CRS so it can exclude from availability and will help debugging on Jarvis.
            if (this.containsDevOnlyQueryParameters()) {
                configServiceParams += "&citem=dev:true";
            }
            this.configServiceParams = configServiceParams;
        });
    }
    /**
     * Function checks if ConfigService is enabled.
     * @returns true if supported else false.
     */
    shouldUseConfigService() {
        // Dev scenario to enable/disable config service
        const queryParams = this.resolverEnvironment
            && this.resolverEnvironment.configServiceParameters
            && this.resolverEnvironment.queryParams;
        if (queryParams && queryParams["enableConfigService"]) {
            this.enableConfigService = queryParams["enableConfigService"] === "true";
        }
    }
    /**
     * Function checks if url has any dev only query string param
     * @returns true if exist else false.
     */
    containsDevOnlyQueryParameters() {
        const queryParams = this.resolverEnvironment.queryParams;
        if (!queryParams) {
            return false;
        }
        const item = queryParams["item"];
        if (item && item.length > 0) {
            return true;
        }
        const citem = queryParams["citem"];
        if (citem) {
            return true;
        }
        const segment = queryParams["pcsonly"];
        if (segment) {
            return true;
        }
        return false;
    }
    /**
     * Returns in-memory configs
     * @returns Map<string, ExperienceConfig>
     */
    getConfigMap() {
        return this.configMap;
    }
    /**
     * Fast helper to try to get it directly from cache so callers do not have to go through
     * additional async layers to get to the result if we've already loaded up the memory map.
     * @param configRef - The config ref to get from memory cache.
     */
    tryGetConfigSync(configRef) {
        if (configRef) {
            const cacheKey = this.getCacheKeyFromConfigRef(configRef);
            return this.configMap.get(cacheKey);
        }
    }
    /**
     * Returns the resolved merged Experience config from CRS.
     * @param {ConfigReference} configRef object
     * @return Promise<ExperienceConfig>
     */
    getConfig(configRef) {
        return __awaiter(this, void 0, void 0, function* () {
            // If request configurations aren't available, throw error.
            if (!this.resolverEnvironment.configServiceParameters || !this.resolverEnvironment.targetScope || !configRef) {
                return Promise.reject(Error("Either of configServiceParameters, targetScope or configRef is not set correctly."));
            }
            // Return if config service is not enabled.
            if (!this.enableConfigService) {
                this._shouldTryFallbackResolver = true;
                return;
            }
            // Get cache key from experience type and other meta data.
            const cacheKey = this.getCacheKeyFromConfigRef(configRef);
            // Return config is already present in the memory cache.
            if (this.configMap.has(cacheKey)) {
                return this.configMap.get(cacheKey);
            }
            // if configPromiseMap has the item, we already have an active promise which is not yet resolved/rejected. Return this promise
            if (this.configPromiseMap.has(cacheKey)) {
                return this.configPromiseMap.get(cacheKey);
            }
            // Process config from network
            const configPromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield this.getConfigTreeFromCrs(configRef);
                    resolve(this.configMap.get(cacheKey));
                }
                catch (error) {
                    this.sendAppError(createError(ResolverAppErrors.GetConfigException, `Exception in getConfig for ${cacheKey}`, error));
                    // If we don't explicitly catch the error, then the promise never gets rejected which might cause issues during experience load time.
                    reject(error);
                }
                finally {
                    this.configPromiseMap.delete(cacheKey);
                }
            }));
            // if caller is using await then this request has been processed and we should not add it to configPromiseMap.
            // if caller is trying to download multiple configs in parallel, then processed should be false.
            this.configPromiseMap.set(cacheKey, configPromise);
            return configPromise;
        });
    }
    /**
     * Fetch configs from CRS and add them to in-memory cache.
     * @param {ConfigReference} configRef object
     */
    getConfigTreeFromCrs(configRef) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Set a 5-second timeout for CRS call
                let timer;
                const timeoutInMs = 5000;
                const timeoutPromise = new Promise((resolve) => {
                    timer = setTimeout(() => {
                        // Log an app error if CRS call timed out
                        const appError = Object.assign(Object.assign({}, CrsFetchTimeout), { pb: Object.assign(Object.assign({}, CrsFetchTimeout.pb), { isWebWorker: this.resolverEnvironment.isThisWebWorkerThread }) });
                        this.sendAppError(createError(appError, "CRS fetch timeout", `ConfigRef: ${this.getCacheKeyFromConfigRef(configRef)}.`));
                        // Return null response so that fallback resolver is automatically invoked
                        return resolve(null);
                    }, timeoutInMs);
                });
                const crsFetchPromise = this.downloadFromCrs(configRef).then((crsResponse) => {
                    if (timer) {
                        clearTimeout(timer);
                    }
                    return crsResponse;
                });
                // Race between CRS and a timeout based promise
                const response = yield Promise.race([crsFetchPromise, timeoutPromise]);
                const configs = response && response.configs;
                if (!configs || isEmpty(configs)) {
                    // If CRS failed to return a valid response, try using the fallback resolver.
                    this._shouldTryFallbackResolver = true;
                    return;
                }
                // Set shouldRetry to false when CRS response is valid
                this._shouldTryFallbackResolver = false;
                for (const configKey in configs) {
                    const config = configs[configKey];
                    if (config && config.properties) {
                        const cacheKey = this.getCacheKeyFromConfigRef(getConfigRefFromKey(configKey));
                        this.configMap.set(cacheKey, config);
                    }
                }
            }
            catch (error) {
                this.sendAppError(createError(ResolverAppErrors.CrsFetchError, "CRS fetch network error", `ConfigRef: ${this.getCacheKeyFromConfigRef(configRef)}. CrsFetchNetworkError: ${this._hadNetworkError}. Error: ${error}`));
            }
        });
    }
    /**
     * Download config(s) from CRS
     * @param {ConfigReference} configRef object
     * @return Promise<ConfigServiceResponse> returns config service response
     */
    downloadFromCrs(configRef) {
        return __awaiter(this, void 0, void 0, function* () {
            const fetchUrl = this.generateCrsUrl(configRef);
            return fetch(fetchUrl)
                .then(response => {
                if (response.ok) {
                    return response.json();
                }
                // Get appropriate CRS headers and instrument in the app error customMessage
                const crsVersion = response.headers.get("X-Crs-BuildVersion") || "";
                const msedgeRef = response.headers.get("x-msedge-ref") || "";
                const errorMessage = `Url: ${fetchUrl} and CRS Version: ${crsVersion} and isWebWorkerPresent ${this.resolverEnvironment.isWebWorkerPresent}`;
                const appError = Object.assign(Object.assign({}, ConfigCrsFetchNon200Response), { pb: Object.assign(Object.assign({}, ConfigCrsFetchNon200Response.pb), { isWebWorker: this.resolverEnvironment.isThisWebWorkerThread, fetchNetworkResponse: {
                            activityId: msedgeRef,
                            statusCode: response.status
                        } }) });
                this.sendAppError(createError(appError, "CRS returned non-200 response", errorMessage));
                return Promise.resolve();
            })
                .catch(error => {
                // If fetch fails to make request or cancelled by the browser, do not make call to fallback resolver.
                this._shouldTryFallbackResolver = false;
                // If CRS fetch had network error
                this._hadNetworkError = true;
                // This throw is required so that parent function can send appropriate app error.
                throw error;
            });
        });
    }
    /**
     * Generate CRS fetch url based on configRef, configServiceParams and configServiceBaseUrl.
     * @param {ConfigReference} configRef object
     * @returns {URL} CRS fetch url
     */
    generateCrsUrl(configRef) {
        const sharedNsQsp = configRef.sharedNs ? `&sharedNs=${configRef.sharedNs}` : "";
        const configRefQsp = `expType=${configRef.experienceType}&expInstance=${configRef.instanceSrc}${sharedNsQsp}`;
        const configServiceRelativeUrl = `?${configRefQsp}&${this.configServiceParams}`;
        const fetchUrl = new URL(configServiceRelativeUrl, this.resolverEnvironment.configServiceParameters.configServiceBaseUrl);
        return fetchUrl.toString();
    }
    /**
     * Send this app error
     * @param appError AppError to send to telemetry
     */
    sendAppError(appError) {
        if (this.resolverEnvironment.appErrorCallback) {
            this.resolverEnvironment.appErrorCallback(appError);
        }
    }
}
//# sourceMappingURL=ConfigService.js.map